import mitt from "mitt";

import { IAuthenticationManager } from "@esri/arcgis-rest-request";
import { StyleFamily } from "./types/StyleFamily.js";
import {
  IStartSessionResponse,
  startNewSession
} from "./utils/startNewSession.js";
import { Writable } from "./utils/writable.js";
import { determineSafetyMargin } from "./utils/detemineSafetyMargin.js";
import {
  DEFAULT_DURATION,
  DEFAULT_CHECK_EXPIRATION_INTERVAL
} from "./utils/defaults.js";

export interface IBasemapSessionParams {
  token: string;
  startSessionUrl: string;
  styleFamily: StyleFamily;
  authentication: IAuthenticationManager | string;
  expires: Date;
  startTime: Date;
  endTime: Date;
  safetyMargin?: number;
  duration?: number;
  autoRefresh?: boolean;
}

export interface IStartSessionParams {
  styleFamily?: StyleFamily;
  authentication: IAuthenticationManager | string;
  safetyMargin?: number;
  duration?: number;
  autoRefresh?: boolean;

  /**
   * The URL to start the session. If not provided, it will use the default URL.
   * @private
   */
  startSessionUrl?: string;
}

/**
 * The base class for all basemap sessions. This class implements the {@linkcode @esri/arcgis-rest-request!IAuthenticationManager} interface and provides methods to start, refresh, and check the expiration of a session.
 * This is not intended to be used directly, but instead is extended by other classes such as {@linkcode BasemapStyleSession} .
 *
 * @abstract
 */
export abstract class BaseSession implements IAuthenticationManager {
  // the static methods for event handlers are used to provide doc via typedoc and do not need to be tested.
  /* istanbul ignore next -- @preserve */
  /**
   * Event handler for when an error occurs during session management.
   */
  static readonly error = function error(e: Error): void {};

  // the static methods for event handlers are used to provide doc via typedoc and do not need to be tested.
  /* istanbul ignore next -- @preserve */
  /**
   * Event handler for when the current session expires and the session token it no longer valid. This event will only fire if {@linkcode BaseSession.checkingExpirationTime}
   * is `true` which is the default. Once this event fires, {@linkcode BaseSession.checkingExpirationTime} will be set to `false` until the session is refreshed with
   * {@linkcode BaseSession.refreshCredentials}.
   *
   * @event expired
   * @param e.token - The session token that expired.
   * @param e.startTime - The start time of the session.
   * @param e.endTime - The end time of the session.
   * @param e.expires - The expiration time of the session.
   */
  static readonly expired = function expired(e: {
    token: string;
    startTime: Date;
    endTime: Date;
    expires: Date;
  }): void {};

  // the static methods for event handlers are used to provide doc via typedoc and do not need to be tested.
  /* istanbul ignore next -- @preserve */
  /**
   * Event handler for when a session refreshes and a new `token` is available.
   *
   * @event refreshed
   * @param e.previous - The previous session details.
   * @param e.previous.token - The previous session token.
   * @param e.previous.startTime - The start time of the previous session.
   * @param e.previous.endTime - The end time of the previous session.
   * @param e.previous.expires - The expiration time of the previous session.
   * @param e.current - The current session details.
   * @param e.current.token - The current session token.
   * @param e.current.startTime - The start time of the current token.
   * @param e.current.endTime - The end time of the current session.
   * @param e.current.expires - The expiration time of the current token.
   */
  static readonly refreshed = function refreshed(e: {
    previous: {
      token: string;
      startTime: Date;
      endTime: Date;
      expires: Date;
    };
    current: {
      token: string;
      startTime: Date;
      endTime: Date;
      expires: Date;
    };
  }): void {};

  /**
   * The portal URL that the session is associated with. This generally is not used but exists to implement the `IAuthenticationManager` interface.
   */
  readonly portal: string;

  /**
   * The style family of the session. This is used to determine the type of basemap styles that are available.
   */
  readonly styleFamily: StyleFamily;

  /**
   * The authentication manager or token used for the session.
   * This can be an instance of {@linkcode @esri/arcgis-rest-request!ApiKeyManager}, {@linkcode @esri/arcgis-rest-request!ArcGISIdentityManager}, {@linkcode @esri/arcgis-rest-request!ApplicationCredentialsManager} or a string token.
   */
  readonly authentication: IAuthenticationManager | string;

  /**
   * The expiration date of the session. This is the {@linkcode BaseSession.endTime} minus the {@linkcode BaseSession.safetyMargin}. This is used internally to determine if the session is expired.
   */
  readonly expires: Date;

  /**
   * The start time of the session. This is the time returned from the API when the session war started.
   */
  readonly startTime: Date;

  /**
   * The end time of the session. This is the time returned from the API when the session will end.
   */
  readonly endTime: Date;

  /**
   * The token for the session.
   */
  readonly token: string;

  /**
   * The URL used to start the session.
   */
  readonly startSessionUrl: string;

  /**
   * The safety margin in milliseconds. This subtracted from the {@linkcode BaseSession.endTime} to get the expiration time of the session.
   */
  readonly safetyMargin: number;

  /**
   * The duration of the session in seconds. This is used to determine how long the session will last when the session is refreshed.
   */
  readonly duration: number;

  /**
   * The interval at which to check the expiration time of the session. This is always 10 seconds or 1/100th of the duration, whichever is smaller.
   */
  private readonly expirationCheckInterval: number;

  /**
   * The ID of the timer used to check the expiration time of the session.
   */
  private expirationTimerId: any = null;

  /**
   * A pending session that is being refreshed. This is used to prevent multiple refreshes from happening at the same time.
   */
  private pendingSession: Promise<IStartSessionResponse> | null = null;

  /**
   * Internal instance of [`mitt`](https://github.com/developit/mitt) used for event handlers. It is recommended to use {@linkcode BasemapSession.on}, {@linkcode BasemapSession.off} or {@linkcode BasemapSession.once} instead of `emitter.`
   */
  private emitter: any;

  /**
   * A handler that is used to automatically refresh the session when it expires.
   */
  private autoRefreshHandler: (() => void) | null = null;

  /**
   * Creates a new instance of the BaseSession class. Generally you should not create an instance of this class directly, but instead use the static methods to start a session or deserialize a session.
   *
   * You may need to create an instance of this class directly if you are  not using the built in deserialize method.
   *
   * @param params - The parameters for the session.
   * @param params.startSessionUrl - The URL to start the session.
   * @param params.token - The token for the session.
   * @param params.styleFamily - The style family of the session.
   * @param params.authentication - The authentication manager or token used for the session.
   * @param params.expires - The expiration date of the session.
   * @param params.startTime - The start time of the session.
   * @param params.endTime - The end time of the session.
   * @param params.safetyMargin - The safety margin in milliseconds.
   * @param params.duration - Indicates if this is a test session.
   */
  constructor(params: IBasemapSessionParams) {
    this.startSessionUrl = params.startSessionUrl;
    this.token = params.token;
    this.styleFamily = params.styleFamily || "arcgis";
    this.authentication = params.authentication;
    this.duration = params.duration || DEFAULT_DURATION;
    this.startTime = params.startTime;
    this.endTime = params.endTime;
    this.expires = params.expires;
    this.safetyMargin = params.safetyMargin;
    this.expirationCheckInterval =
      Math.min(this.duration / 100, DEFAULT_CHECK_EXPIRATION_INTERVAL) * 1000;
    this.emitter = mitt();
  }

  /**
   * Checks if the session is expired. If it is expired, it emits an "expired" event and disables expiration time checking. The event will fire **before** the method returns true.
   *
   * @returns {boolean} - Returns true if the session is expired, otherwise false.
   */
  isSessionExpired() {
    if (this.isExpired) {
      this.disableCheckingExpirationTime();

      this.emitter.emit("expired", {
        token: this.token,
        startTime: this.startTime,
        endTime: this.endTime,
        expires: this.expires
      });
    }

    return this.isExpired;
  }

  /**
   * Starts checking the expiration time of the session. This will check the expiration time immediately and then on an interval.
   * If the session is expired, it will emit an "expired" event.
   */
  enableCheckingExpirationTime() {
    const check = () => {
      this.isSessionExpired();
    };

    if (!this.expirationTimerId) {
      this.expirationTimerId = setInterval(
        check,
        // check every 10 seconds or 1/100th of the duration, whichever is smaller
        this.expirationCheckInterval
      ); // check immediately then on an interval
    }

    setTimeout(() => {
      check(); // check immediately after starting the interval
    }, 10);

    return this.expirationTimerId; // return the timer ID so it can be stopped later
  }

  /**
   * Stops checking the expiration time of the session. This will clear the `setInterval()` that was started by {@linkcode BaseSession.enableCheckingExpirationTime}.
   */
  disableCheckingExpirationTime() {
    if (this.expirationTimerId) {
      clearInterval(this.expirationTimerId);
      this.expirationTimerId = null;
    }
  }

  /**
   * Starts a new session using the provided parameters and returns an instance of the session class.
   *
   * @param params - The parameters for starting the session.
   * @param SessionClass - The class to use for the session.
   * @returns A promise that resolves to an instance of the session class.
   */
  protected static async startSession<T extends BaseSession>(
    {
      startSessionUrl,
      styleFamily = "arcgis",
      authentication,
      safetyMargin,
      duration = DEFAULT_DURATION,
      autoRefresh = false
    }: {
      startSessionUrl?: string;
      styleFamily?: StyleFamily;
      authentication: IAuthenticationManager | string;
      safetyMargin?: number;
      duration?: number;
      autoRefresh?: boolean;
    },
    SessionClass: new (params: IBasemapSessionParams) => T
  ): Promise<T> {
    if (duration < 10) {
      throw new Error("Session duration must be at least 10 seconds.");
    }

    if (duration > 43200) {
      throw new Error(
        "Session duration cannot exceed 12 hours (43200 seconds)."
      );
    }

    const sessionResponse = await startNewSession({
      startSessionUrl,
      styleFamily,
      authentication,
      duration
    });
    const actualSafetyMargin = determineSafetyMargin(duration, safetyMargin);

    const session = new SessionClass({
      startSessionUrl: startSessionUrl,
      token: sessionResponse.sessionToken,
      styleFamily,
      authentication,
      safetyMargin: actualSafetyMargin,
      expires: new Date(sessionResponse.endTime - actualSafetyMargin * 1000),
      startTime: new Date(sessionResponse.startTime),
      endTime: new Date(sessionResponse.endTime),
      duration
    });

    session.enableCheckingExpirationTime();

    if (autoRefresh) {
      session.enableAutoRefresh();
    }

    return session as T;
  }

  /**
   * Indicates if the session is currently checking for expiration time.
   *
   * @returns {boolean} - Returns true if the session is checking for expiration time, otherwise false.
   */
  get checkingExpirationTime(): boolean {
    return !!this.expirationTimerId;
  }

  /**
   * Returns the number of seconds until the session is no longer valid rounded down. If the session is expired, it will return 0.
   */
  get secondsUntilExpiration(): number {
    return Math.floor(this.millisecondsUntilExpiration / 1000);
  }

  /**
   * Returns the number of milliseconds until the session token is no longer valid. If the session is expired, it will return 0.
   */
  get millisecondsUntilExpiration(): number {
    if (this.isExpired) {
      return 0;
    }

    const now = new Date();
    const millisecondsLeft = this.endTime.getTime() - now.getTime();

    return millisecondsLeft;
  }

  /**
   * Checks if the session is expired.
   *
   */
  get isExpired(): boolean {
    return this.expires < new Date();
  }

  /**
   * Gets the session token. If the session is expired, it will refresh the credentials and return the new token.
   *
   * @returns A promise that resolves to the session token.
   */
  getToken(): Promise<string> {
    if (this.isExpired) {
      return this.refreshCredentials().then(() => this.token);
    }

    return Promise.resolve(this.token);
  }

  /**
   * Indicates if the session can be refreshed. This is always true for this basemap sessions.
   *
   * @returns {boolean} - Always returns true.
   */
  get canRefresh(): boolean {
    return true;
  }

  /**
   * Indicates if the session is set to automatically refresh when it expires.
   *
   * @returns {boolean} - Returns true if auto-refresh is enabled, otherwise false.
   */
  get autoRefresh(): boolean {
    return !!this.autoRefreshHandler && !!this.expirationTimerId;
  }

  /**
   * Refreshes the session credentials by starting a new session.
   * This will emit a "refreshed" event with the previous and current session details.
   *
   * @returns A promise that resolves to the current instance of the session.
   */
  async refreshCredentials(): Promise<this> {
    if (this.pendingSession) {
      // if there is a pending session, wait for it to resolve
      await this.pendingSession;
      return this;
    }

    // @TODO switch this to structured clone when we upgrade to Node 20+ types so we don't have to parse the dates later
    const previous = JSON.parse(
      JSON.stringify({
        token: this.token,
        startTime: this.startTime,
        endTime: this.endTime,
        expires: this.expires
      })
    );

    try {
      this.pendingSession = startNewSession({
        startSessionUrl: this.startSessionUrl,
        styleFamily: this.styleFamily,
        authentication: this.authentication,
        duration: this.duration
      });

      const newSession = await this.pendingSession;

      this.pendingSession = null; // reset the pending session

      this.setToken(newSession.sessionToken);
      this.setStartTime(new Date(newSession.startTime));
      this.setEndTime(new Date(newSession.endTime));
      this.setExpires(new Date(newSession.endTime - this.safetyMargin * 1000));

      this.enableCheckingExpirationTime(); // restart checking expiration time after refreshing credentials

      this.emitter.emit("refreshed", {
        previous: {
          token: previous.token,
          startTime: new Date(previous.startTime),
          endTime: new Date(previous.endTime),
          expires: new Date(previous.expires)
        },
        current: {
          token: this.token,
          startTime: this.startTime,
          endTime: this.endTime,
          expires: this.expires
        }
      });
    } catch (error) {
      this.emitter.emit("error", error);
      throw error;
    }

    return this;
  }
  /**
   * Enables auto-refresh for the session. This will automatically refresh the session when it expires.
   * It will also start checking the expiration time of the session if it is not already started via {@linkcode BaseSession.enableCheckingExpirationTime}.
   */
  enableAutoRefresh() {
    if (!this.expirationTimerId) {
      this.enableCheckingExpirationTime();
    }

    this.autoRefreshHandler = () => {
      this.refreshCredentials().catch((error: Error) => {
        this.emitter.emit("error", error);
      });
    };

    this.on("expired", this.autoRefreshHandler);
  }

  /**
   * Disables auto-refresh for the session. This will stop automatically refreshing the session when it expires.
   * This will  **not** stop checking the expiration time of the session. If you want to stop automated expiration
   * checking, call {@linkcode BaseSession.disableCheckingExpirationTime} after calling this method.
   */
  disableAutoRefresh() {
    if (this.autoRefreshHandler) {
      this.off("expired", this.autoRefreshHandler);
      this.autoRefreshHandler = null;
    }
  }

  /**
   * Removes all event listeners and disables auto-refresh and expiration time checking. This is useful for cleaning up the session when it is no longer needed or replaced with a new session.
   */
  destroy() {
    this.disableAutoRefresh();
    this.disableCheckingExpirationTime();
    this.emitter.off("expired");
    this.emitter.off("refreshed");
    this.emitter.off("error");
    this.emitter.off("*");
  }

  /**
   * A handler that listens for an eventName and returns custom handler.
   *
   * @param event A string of what event to listen for.
   * @param handler A function of what to do when eventName was called.
   */
  on(event: "refreshed", handler: typeof BaseSession.refreshed): void;
  on(event: "expired", handler: typeof BaseSession.expired): void;
  on(event: "error", handler: typeof BaseSession.error): void;
  on(
    event: string,
    handler:
      | typeof BaseSession.refreshed
      | typeof BaseSession.expired
      | typeof BaseSession.error
  ) {
    this.emitter.on(event, handler);
    this.isSessionExpired(); // check if the session is expired immediately after adding the handler
  }

  /**
   * A handler that listens for an event once and returns a custom handler. Events listened to with this method cannot be removed with {@linkcode BaseSession.off}.
   *
   * @param event A string of what event to listen for.
   * @param handler A function of what to do when event was called.
   */
  once(event: "refreshed", handler: typeof BaseSession.refreshed): void;
  once(event: "expired", handler: typeof BaseSession.expired): void;
  once(event: "error", handler: typeof BaseSession.error): void;
  once(
    event: string,
    handler:
      | typeof BaseSession.refreshed
      | typeof BaseSession.expired
      | typeof BaseSession.error
  ) {
    const fn = (e: any) => {
      this.emitter.off(event, fn);
      handler(e);
    };

    this.emitter.on(event, fn);
  }

  /**
   * A handler that will remove a listener from a given event.
   *
   * @param event A string of what event to listen for.
   * @param handler A function of what to do when event was called.
   */
  off(event: "refreshed", handler: typeof BaseSession.refreshed): void;
  off(event: "expired", handler: typeof BaseSession.expired): void;
  off(event: "error", handler: typeof BaseSession.error): void;
  off(
    event: string,
    handler:
      | typeof BaseSession.refreshed
      | typeof BaseSession.expired
      | typeof BaseSession.error
  ) {
    this.emitter.off(event, handler);
  }

  /**
   * These private methods are used to set the internal state of the session.
   */
  private setToken(token: string) {
    (this as Writable<typeof this>).token = token;
  }
  private setStartTime(startTime: Date) {
    (this as Writable<typeof this>).startTime = startTime;
  }
  private setEndTime(endTime: Date) {
    (this as Writable<typeof this>).endTime = endTime;
  }
  private setExpires(expires: Date) {
    (this as Writable<typeof this>).expires = expires;
  }
}
